import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {from, Observable, switchMap} from 'rxjs';
import {environment} from '../../environments/environment';

export interface ModerationQueuePage {
  items: AdminListingView[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

export interface AdminListingView {
  itemId: string;
  name: string;
  url: string;
  status: string;
  statusReason?: string;
  value?: number;
  sellerName?: string;
  sellerUrl?: string;
  createdOn?: string;
  publishedOn?: string;
}

export interface ModerationListingDetail extends AdminListingView {
  description?: string;
  photo?: string;
  sellerEmail?: string;
  categoryPath?: string;
  locationLabel?: string;
  history?: ModerationReviewView[];
}

export interface ModerationReviewView {
  reviewId: string;
  moderatorId?: string;
  action: string;
  reason?: string;
  createdOn?: string;
}

export interface SellerListingView {
  itemId: string;
  name: string;
  url: string;
  status: string;
  statusReason?: string;
  value?: number;
  photo?: string;
  createdOn?: string;
  publishedOn?: string;
  totalViews: number;
  viewsLast30Days: number;
  inquiriesLast30Days?: number;
  favoritesLast30Days?: number;
  impressionsLast30Days?: number;
  ctrPercent?: number;
  conversionPercent?: number;
  featured?: boolean;
}

export interface DailyEngagementCount {
  date: string;
  views: number;
  inquiries?: number;
  favorites?: number;
  impressions?: number;
}

export interface SellerAnalyticsView {
  fromDate?: string;
  toDate?: string;
  periodDays?: number;
  totalViews: number;
  viewsLast30Days: number;
  inquiriesInPeriod?: number;
  favoritesInPeriod?: number;
  impressionsInPeriod?: number;
  ctrPercent?: number;
  conversionPercent?: number;
  previousViewsInPeriod?: number;
  previousInquiriesInPeriod?: number;
  previousFavoritesInPeriod?: number;
  previousImpressionsInPeriod?: number;
  previousCtrPercent?: number;
  previousConversionPercent?: number;
  publishedListings: number;
  pendingListings: number;
  rejectedListings: number;
  listings?: SellerListingView[];
  topListings: SellerListingView[];
  viewsByDay: DailyEngagementCount[];
}

export interface SellerListingAnalyticsView {
  listing: SellerListingView;
  fromDate?: string;
  toDate?: string;
  periodDays?: number;
  viewsByDay: DailyEngagementCount[];
}

export interface SellerAnalyticsQuery {
  days?: number;
  from?: string;
  to?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SellerService {
  private readonly baseUrlPromise: Promise<string>;

  constructor(private http: HttpClient) {
    this.baseUrlPromise = environment.then((env: any) => env.backendURL + '/api/seller');
  }

  getListings(query: SellerAnalyticsQuery | number = 30): Observable<SellerListingView[]> {
    const params = this.toParams(query);
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.get<SellerListingView[]>(`${baseUrl}/listings`, {params}))
    );
  }

  getAnalytics(query: SellerAnalyticsQuery | number = 30): Observable<SellerAnalyticsView> {
    const params = this.toParams(query);
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.get<SellerAnalyticsView>(`${baseUrl}/analytics`, {params}))
    );
  }

  getListingAnalytics(itemId: string, query: SellerAnalyticsQuery | number = 30): Observable<SellerListingAnalyticsView> {
    const params = this.toParams(query);
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.get<SellerListingAnalyticsView>(
        `${baseUrl}/listings/${itemId}/analytics`, {params}))
    );
  }

  resubmit(itemId: string): Observable<SellerListingView> {
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.post<SellerListingView>(`${baseUrl}/listings/${itemId}/resubmit`, {}))
    );
  }

  private toParams(query: SellerAnalyticsQuery | number): HttpParams {
    const q: SellerAnalyticsQuery = typeof query === 'number' ? {days: query} : (query || {});
    let params = new HttpParams();
    if (q.from && q.to) {
      return params.set('from', q.from).set('to', q.to);
    }
    return params.set('days', String(q.days ?? 30));
  }
}
